import {
  GrainUsageTypeEnum,
  ROUTE_PARTIES_GRAIN_EDIT,
} from '@/common/constants';
import { EditIcon } from '@/common/icons/EditIcon';
import { RemoveIcon } from '@/common/icons/RemoveIcon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { IParty } from '@/features/parties-grain/models';
import usePartyGrainStore from '@/features/parties-grain/store/usePartyGrainStore';

import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const useColumns = (): ColumnDef<IParty | any>[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deletePartyGrain, fetchPartiesGrain, storageTypesBySeed } =
    usePartyGrainStore();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('activeTab');
  const pageSize = Number(searchParams.get('size')) || 10;
  const currentPage = Number(searchParams.get('page')) || 1;

  const deleteItem = async (id: string) => {
    try {
      await deletePartyGrain(id);
      fetchPartiesGrain({
        page: currentPage,
        size: pageSize,
        usage_type: activeTab as GrainUsageTypeEnum,
      });
    } catch (error) {
      console.error('Error deleting party:', error);
    }
  };

  return useMemo(
    () => [
      {
        accessorKey: 'place_number',
        header: () => t('place.number'),
      },
      {
        accessorKey: 'party_number',
        header: () => t('party.number'),
      },
      {
        accessorKey: 'crop_type_name',
        header: () => t('selection.type'),
      },
      {
        accessorKey: 'crop_generation_name',
        header: () => t('generation'),
      },
      {
        accessorKey: 'plant_name',
        header: () => t('type.of.crop'),
        cell: ({ row }) => row.original.plant_name || '-',
      },
      {
        accessorKey: 'storage_type',
        header: () => t('storage.type'),
        cell: ({ row }) => {
          return storageTypesBySeed?.find(
            (el) => el.value === row.original.storage_type,
          )?.label;
        },
      },
      {
        accessorKey: 'class',
        header: () => t('class'),
      },
      {
        accessorKey: 'capacity',
        header: () => t('capacity'),
      },
      {
        accessorKey: 'transit_in_loaded',
        header: () => "Yuklangan g'alla",
        cell: ({ row }) =>
          Number(row.original?.receptions_loaded)?.toFixed(1) || '-',
      },
      {
        accessorKey: 'free_space',
        header: () => t('free.space'),
        cell: ({ row }) => Number(row.original?.free_space)?.toFixed(1) || '-',
      },
      {
        id: 'action',
        header: () => <div className="text-center">{t('action')}</div>,
        cell: ({ row }) => (
          <div className="flex-center-center">
            <Button
              size="icon"
              variant="ghost"
              className="size-8 !rounded-10"
              onClick={() =>
                navigate(
                  ROUTE_PARTIES_GRAIN_EDIT +
                    '/' +
                    row.original.id +
                    '?activeTab=' +
                    activeTab,
                )
              }
            >
              <EditIcon />
            </Button>
            {/* <PermissionWrapper permissions={[PermissionsEnum.DELETE_PARTIES]}> */}
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8 !rounded-10"
                >
                  <RemoveIcon />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{t('confirm.delete')}</DialogTitle>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">{t('cancel')}</Button>
                  </DialogClose>
                  <Button onClick={() => deleteItem(row.original?.id)}>
                    {t('yes')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {/* </PermissionWrapper> */}
          </div>
        ),
      },
    ],
    [t, deleteItem],
  );
};
